/**
 * internalApiAuth.ts — 社内 API（/sys/* の管理系 PHP）の認証をスタッフのログイントークンに切り替える（2026-10-09）
 *
 * 背景: 社内 API は共有キー（?api_key=）で守られていたが、その値がこのフロントのソースに直書きされ、
 * ログイン前の画面でも配信される公開バンドルに入っていた。誰でも LINE の会話・画像の取得、公式アカウントとしての
 * 送信、予約キャンセル、顧客DB・メール受信箱の閲覧ができる状態だった。
 *
 * 対策: サーバー側（sys/internal_auth.php）はスタッフの Bearer トークンを backend /auth/me で検証する。
 * 呼び出し箇所が約80か所あるため、個別に書き換える代わりに fetch を1か所で包み、
 * 「/sys/ 配下で api_key パラメータ付きのリクエスト」だけを対象に
 *   - api_key パラメータを取り除き（値は空。秘密はもうフロントに無い）
 *   - Authorization: Bearer <authToken> を付ける
 * それ以外のリクエスト（backend API・外部サービス等）には一切触れない。
 *
 * 2026-10-31: 決済 API（/sys/api/payment/）はもともと認証なし（api_key も無し）で呼んでいたため、
 * api_key の有無に関係なくこのパス配下も対象にする（サーバー側は config.php で Bearer 必須にした）。
 * 呼び出し元（useSquarePaymentData・SquarePaymentInfo・MobileSquareInfo・PaymentCheck）は個別に書き換えない。
 */

const AUTH_TOKEN_KEY = 'authToken';

declare global {
  interface Window {
    __ncInternalApiAuthInstalled?: boolean;
  }
}

/** 対象なら api_key を除いた URL を返す。対象外は null */
export function rewriteInternalApiUrl(rawUrl: string, base: string): string | null {
  let u: URL;
  try {
    u = new URL(rawUrl, base);
  } catch {
    return null;
  }
  const isPaymentApi = u.pathname.includes('/sys/api/payment/');
  if (!isPaymentApi && (!u.pathname.includes('/sys/') || !u.searchParams.has('api_key'))) return null;
  u.searchParams.delete('api_key');
  return u.toString();
}

/** 認証ヘッダ付きの headers を作る（呼び出し側が Authorization を指定済みなら尊重する） */
export function withStaffAuth(headersInit: HeadersInit | undefined, token: string | null): Headers {
  const headers = new Headers(headersInit);
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export function installInternalApiAuth(): void {
  if (typeof window === 'undefined' || window.__ncInternalApiAuthInstalled) return;
  window.__ncInternalApiAuthInstalled = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const rewritten = rewriteInternalApiUrl(rawUrl, window.location.href);
    if (rewritten === null) return originalFetch(input, init);

    let token: string | null = null;
    try {
      token = window.localStorage.getItem(AUTH_TOKEN_KEY);
    } catch {
      token = null; // ストレージ不可（プライベートモード等）。サーバーが 401 を返し、画面側のエラー処理に乗る
    }
    const baseHeaders = init?.headers ?? (input instanceof Request ? input.headers : undefined);
    const headers = withStaffAuth(baseHeaders, token);
    if (input instanceof Request) {
      return originalFetch(new Request(rewritten, input), { ...init, headers });
    }
    return originalFetch(rewritten, { ...init, headers });
  };
}
